import earcut from "earcut";

const EMPTY = Object.freeze({
  positions: new Float32Array(),
  normals: new Float32Array(),
  triangleCount: 0,
  contourCount: 0,
});

function pointKey(point, tolerance) {
  return `${Math.round(point[0] / tolerance)},${Math.round(point[1] / tolerance)}`;
}

function signedArea(polygon) {
  let area = 0;
  for (let index = 0; index < polygon.length; index += 1) {
    const first = polygon[index];
    const second = polygon[(index + 1) % polygon.length];
    area += first[0] * second[1] - second[0] * first[1];
  }
  return area / 2;
}

function pointInPolygon(point, polygon) {
  let inside = false;
  for (let first = polygon.length - 1, second = 0; second < polygon.length; first = second, second += 1) {
    const a = polygon[first];
    const b = polygon[second];
    const crosses = (a[1] > point[1]) !== (b[1] > point[1]);
    if (crosses && point[0] < (b[0] - a[0]) * (point[1] - a[1]) / (b[1] - a[1]) + a[0]) {
      inside = !inside;
    }
  }
  return inside;
}

function intersectionSegments(positions, planeX, tolerance) {
  // Nudge the sampling plane away from symmetry vertices while drawing the
  // resulting cap at the requested plane. The difference is sub-pixel.
  const sampleX = planeX + tolerance * 0.173;
  const segments = [];
  const segmentKeys = new Set();
  for (let offset = 0; offset < positions.length; offset += 9) {
    const vertices = [
      [positions[offset], positions[offset + 1], positions[offset + 2]],
      [positions[offset + 3], positions[offset + 4], positions[offset + 5]],
      [positions[offset + 6], positions[offset + 7], positions[offset + 8]],
    ];
    const points = [];
    for (let edge = 0; edge < 3; edge += 1) {
      const first = vertices[edge];
      const second = vertices[(edge + 1) % 3];
      const firstDistance = first[0] - sampleX;
      const secondDistance = second[0] - sampleX;
      if ((firstDistance < 0) === (secondDistance < 0)) continue;
      const fraction = firstDistance / (firstDistance - secondDistance);
      points.push([
        first[1] + (second[1] - first[1]) * fraction,
        first[2] + (second[2] - first[2]) * fraction,
      ]);
    }
    if (points.length !== 2) continue;
    const firstKey = pointKey(points[0], tolerance);
    const secondKey = pointKey(points[1], tolerance);
    if (firstKey === secondKey) continue;
    const segmentKey = firstKey < secondKey
      ? `${firstKey}|${secondKey}`
      : `${secondKey}|${firstKey}`;
    if (segmentKeys.has(segmentKey)) continue;
    segmentKeys.add(segmentKey);
    segments.push({keys: [firstKey, secondKey], points});
  }
  return segments;
}

function stitchContours(segments) {
  const adjacency = new Map();
  const points = new Map();
  segments.forEach((segment, index) => {
    segment.keys.forEach((key, endpoint) => {
      points.set(key, segment.points[endpoint]);
      if (!adjacency.has(key)) adjacency.set(key, []);
      adjacency.get(key).push(index);
    });
  });

  const used = new Uint8Array(segments.length);
  const contours = [];
  for (let startEdge = 0; startEdge < segments.length; startEdge += 1) {
    if (used[startEdge]) continue;
    used[startEdge] = 1;
    const startKey = segments[startEdge].keys[0];
    let currentKey = segments[startEdge].keys[1];
    const contourKeys = [startKey, currentKey];
    let closed = false;
    for (let guard = 0; guard <= segments.length; guard += 1) {
      if (currentKey === startKey) {
        closed = true;
        break;
      }
      const nextEdge = (adjacency.get(currentKey) || []).find((index) => !used[index]);
      if (nextEdge === undefined) break;
      used[nextEdge] = 1;
      const edge = segments[nextEdge].keys;
      currentKey = edge[0] === currentKey ? edge[1] : edge[0];
      contourKeys.push(currentKey);
    }
    if (!closed) continue;
    contourKeys.pop();
    const contour = contourKeys.map((key) => points.get(key));
    if (contour.length >= 3 && Math.abs(signedArea(contour)) > 1e-6) contours.push(contour);
  }
  return contours;
}

function contourHierarchy(contours) {
  const records = contours.map((points, index) => ({
    index,
    points,
    area: Math.abs(signedArea(points)),
    parent: null,
    depth: null,
  }));
  for (const record of records) {
    let parent = null;
    for (const candidate of records) {
      if (candidate === record || candidate.area <= record.area) continue;
      if (!pointInPolygon(record.points[0], candidate.points)) continue;
      if (!parent || candidate.area < parent.area) parent = candidate;
    }
    record.parent = parent;
  }
  const depthOf = (record) => {
    if (record.depth !== null) return record.depth;
    record.depth = record.parent ? depthOf(record.parent) + 1 : 0;
    return record.depth;
  };
  records.forEach(depthOf);
  return records;
}

/** Build a filled YZ cap for an unindexed STL triangle array at assembled X. */
export function buildSectionCap(positions, planeX, tolerance = 1e-3) {
  if (!(positions instanceof Float32Array) || positions.length < 9) return EMPTY;
  const contours = stitchContours(intersectionSegments(positions, planeX, tolerance));
  if (!contours.length) return EMPTY;
  const records = contourHierarchy(contours);
  const capPositions = [];
  const capNormals = [];

  for (const outer of records.filter((record) => record.depth % 2 === 0)) {
    const rings = [outer.points];
    const holes = records.filter((record) => record.parent === outer && record.depth % 2 === 1);
    rings.push(...holes.map((record) => record.points));
    const flat = [];
    const holeIndices = [];
    rings.forEach((ring, ringIndex) => {
      if (ringIndex > 0) holeIndices.push(flat.length / 2);
      for (const point of ring) flat.push(point[0], point[1]);
    });
    const indices = earcut(flat, holeIndices, 2);
    for (const index of indices) {
      capPositions.push(planeX, flat[index * 2], flat[index * 2 + 1]);
      capNormals.push(1, 0, 0);
    }
  }

  return {
    positions: new Float32Array(capPositions),
    normals: new Float32Array(capNormals),
    triangleCount: capPositions.length / 9,
    contourCount: contours.length,
  };
}
